import { useContext, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { axiosPut } from '../../../../utils/axiosPut';
import { axiosGet } from '../../../../utils/axiosGet';
import { axiosPost } from '../../../../utils/axiosPost';
import { CommonCollections } from '../../../../App';
import { useStaticCollections } from '../../../../context/StaticCollections';
import { useLoggedInUserContextProvider } from '../../../../context/LoggedInUserContext';
import { useRefereeCollections } from '../../../../context/RefereeReportCollections';
import { useAdminActionCollections } from '../../../../context/AdminActionCollections';
import { useUserPermissions } from '../../../hooks/useUserPermissions';
import { devError } from '../../../../utils/devLog';

const useRefereeReportWizard = ({
  setValue,
  watch,
  trigger,
  formState,
  setFormState,
  setHideButtons,
}) => {
  const { userDetails, originalUserDetails } = useLoggedInUserContextProvider();
  const { isAdmin } = useUserPermissions();

  const {
    fellows,
    usersArray,
    nonAasReferees,
    electionTypes,
    refereeStatuses,
    refereeReportArchivedArray,
  } = useStaticCollections();

  const location = useLocation();
  const isArchiveView = location.pathname.includes('referee-report-archive');

  const { setRefereeReportId, setIsFormSubmitted } =
    useContext(CommonCollections);

  const {
    candidates,
    nominationArray,
    refereeReports,
    nonAdminRefereeReportId,
    setNonAdminRefereeReportId,
  } = useRefereeCollections();

  const { refereeReportId } = useContext(CommonCollections);

  const [formStatus, setFormStatus] = useState(1);
  const { enableRefereeReportUploads } = useAdminActionCollections();
  const [refereeReportData, setRefereeReportData] = useState(null);
  const [reportId, setReportId] = useState(null);
  const [notificationMessage, setNotificationMessage] = useState('');
  const [notificationTitle, setNotificationTitle] = useState('');
  const [showSubmitModal, setShowSubmitModal] = useState(false);
  const [pendingFormData, setPendingFormData] = useState(null);
  const [sendEmailToReferee, setSendEmailToReferee] = useState(false);

  const watchedRefereeActions = watch('refereeActions');

  useEffect(() => {
    if (watchedRefereeActions) {
      trigger();
    }
  }, [watchedRefereeActions, trigger]);

  const getNotificationMessage = (action) => {
    const messages = {
      1: {
        title: 'Pending Referee report',
        message: 'Referee report is pending. Action is required.',
      },
      2: {
        title: 'Report Submitted',
        message: 'Referee report submitted successfully.',
      },
      3: {
        title: 'Report Declined',
        message: 'Referee report has been declined.',
      },
      4: {
        title: 'Existing Report Used',
        message: 'Existing report has been used successfully.',
      },
      5: {
        title: 'Extension Requested',
        message: 'Extension request has been submitted successfully.',
      },
      6: {
        title: 'Extension Approved',
        message: 'Extension has been approved.',
      },
    };

    return (
      messages[action] || {
        title: 'Success',
        message: 'Action completed successfully.',
      }
    );
  };

  const getDataFromCollections = async (targetReportId) => {
    const sourceArray = isArchiveView
      ? refereeReportArchivedArray
      : refereeReports;

    const refereeReport = sourceArray.find(
      (report) => report.id === targetReportId,
    );
    if (!refereeReport) return null;

    const { nominationId } = refereeReport.attributes;
    const nomination = nominationArray.find((nom) => nom.id === nominationId);
    if (!nomination) return null;

    const { candidateId } = nomination.attributes;
    if (!candidateId) return null;

    const nominationStatesResponse = await axiosGet(
      `nomination-states?filters[nominationId][$eq]=${nominationId}&populate=*`,
    );

    return {
      refereeReport,
      nomination,
      candidate: candidates[candidateId],
      nominationState: nominationStatesResponse.data.data,
    };
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        if (refereeReportId && userDetails) {
          const collectionData = await getDataFromCollections(refereeReportId);

          if (collectionData) {
            const { refereeReport, nomination, candidate, nominationState } =
              collectionData;
            const { electionTypeId } = nomination.attributes;
            const { refereeStatusId } = refereeReport.attributes;

            const refereeStatus = refereeStatuses[refereeStatusId]
              ? {
                  id: refereeStatusId,
                  attributes: { name: refereeStatuses[refereeStatusId] },
                }
              : null;

            refereeReport.nomination = nomination;
            setFormState(refereeReport);
            setValue('electionTypeId', electionTypeId);

            const refereeData = {
              refereeReport,
              candidate,
              nominationState,
              electionType: electionTypes[electionTypeId],
              refereeStatus,
            };

            setRefereeReportData(refereeData);
            setReportId(refereeReportId);
            setRefereeReportId(null);
          }
        }
      } catch (error) {
        devError('An error occurred:', error);
      }

      if (!isAdmin) {
        setHideButtons(!enableRefereeReportUploads);
      }

      trigger();
    };

    setFormStatus(1);
    fetchData();
  }, [
    refereeReportId,
    userDetails,
    setValue,
    isArchiveView,
    trigger,
    setRefereeReportId,
    nonAdminRefereeReportId,
    setNonAdminRefereeReportId,
    enableRefereeReportUploads,
    electionTypes,
    refereeStatuses,
    candidates,
    nominationArray,
    refereeReports,
    refereeReportArchivedArray,
    isAdmin,
    setFormState,
    setHideButtons,
  ]);

  useEffect(() => {
    if (formState?.attributes?.refereeStatusId) {
      const notification = getNotificationMessage(
        formState.attributes.refereeStatusId,
      );
      setNotificationMessage(notification.message);
      setNotificationTitle(notification.title);
    }
  }, [formState?.attributes?.refereeStatusId]);

  const handleSuccessfulSubmission = (action) => {
    setRefereeReportId(reportId);
    setNonAdminRefereeReportId(reportId);

    const notification = getNotificationMessage(action);
    setNotificationMessage(notification.message);
    setNotificationTitle(notification.title);

    setTimeout(() => {
      setIsFormSubmitted(true);
    }, 2000);
  };

  const getConfirmationMessage = (action) => {
    const messages = {
      2: 'Are you sure you want to submit this referee report?',
      3: 'Are you sure you want to decline this referee report?',
      4: 'Are you sure you want to use the existing report?',
      5: 'Are you sure you want to request an extension?',
    };

    return messages[action] || 'Are you sure you want to submit?';
  };

  const handleSubmitClick = (data) => {
    setPendingFormData(data);
    setSendEmailToReferee(false);
    setShowSubmitModal(true);
  };

  const handleConfirmSubmit = async () => {
    if (!pendingFormData) return;
    setShowSubmitModal(false);
    setSendEmailToReferee(false);
    try {
      await performSubmit(pendingFormData, sendEmailToReferee);
    } finally {
      setPendingFormData(null);
    }
  };

  const handleCancelSubmit = () => {
    setShowSubmitModal(false);
    setPendingFormData(null);
    setSendEmailToReferee(false);
  };

  const performSubmit = (data, sendEmail = false) => {
    if (!refereeReportData || !reportId) {
      devError('Referee report data not available');
      setNotificationTitle('Cannot submit');
      setNotificationMessage(
        'Referee report data is not yet loaded. Please wait a moment and try again.',
      );
      return;
    }

    const { refereeReport, candidate } = refereeReportData;
    data.refereeUser = refereeReport.attributes.refereeFellowId
      ? fellows[refereeReport.attributes.refereeFellowId]
      : nonAasReferees[refereeReport.attributes.nonAasRefereeId];
    data.referee = usersArray.find(
      (user) => user.id === refereeReport.attributes.refereeUserId,
    );
    data.candidate = candidate;
    data.refereeStatusId = data.refereeActions;
    data.decline = false;
    data.useExistingReport = false;
    data.requestExtension = false;
    data.testEmail = originalUserDetails.email;

    if (isAdmin || userDetails?.isMasquerade) {
      data.sendEmailToReferee = sendEmail;
    }

    switch (data.refereeActions) {
      case '2':
        if (!data.report) data.report = {};
        data.report.id = data?.reportFileId?.id;
        data.report.name = data?.reportFileName;
        data.refereeReportUploadedRound = new Date();
        break;

      case '3':
        data.refereeDeclineSubmittedYear = new Date();
        data.decline = true;
        break;

      case '4':
        data.useExistingReport = true;
        if (formState?.attributes?.report?.data) {
          data.report = formState.attributes.report.data.id;
        }
        break;

      case '5':
        data.requestExtension = true;
        break;

      default:
    }

    const refereeData = {
      data: {
        ...data,
        reportId,
      },
    };

    if (data.refereeActions === '5') {
      return axiosPost(`referee-report-request-extension`, {
        reportId,
        extensionReason: data.extensionReason,
        testEmail: originalUserDetails.email,
      })
        .then((response) => {
          if (response.status === 200) {
            handleSuccessfulSubmission(data.refereeActions);
          }
        })
        .catch((error) => {
          devError('Error requesting extension:', error);

          if (error.response?.status === 400) {
            setNotificationMessage(
              error.response?.data?.error?.message ||
                'Extension request already submitted.',
            );
          } else {
            setNotificationMessage(
              'Failed to submit extension request. Please try again.',
            );
          }
          setNotificationTitle('Error');
        });
    }
    return axiosPut(`update-referee-report/${reportId}/`, {
      refereeData,
    })
      .then((response) => {
        if (response?.status === 200) {
          handleSuccessfulSubmission(data.refereeActions);
        }
      })
      .catch((error) => {
        devError('Error submitting referee report:', error);
        setNotificationMessage(
          'Failed to submit referee report. Please try again.',
        );
        setNotificationTitle('Error');
      });
  };

  return {
    userDetails,
    isAdmin,
    formStatus,
    refereeReportData,
    notificationMessage,
    notificationTitle,
    showSubmitModal,
    setShowSubmitModal,
    pendingFormData,
    sendEmailToReferee,
    setSendEmailToReferee,
    handleSubmitClick,
    handleConfirmSubmit,
    handleCancelSubmit,
    getConfirmationMessage,
  };
};

export default useRefereeReportWizard;
